// RoomContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from "react";
import {
  useHotelService as useService,
  useHotelServices as useServices,
  useCreateHotelService as useCreateService,
  useUpdateHotelService as useUpdateService,
  useDeleteHotelService as useDeleteService,
  useActivateHotelService as useActivateService,
  useDeactivateHotelService as useDeactivateService,
  useCheckinServices,
} from "@/hooks/useHotelServices";
import {
  HotelServiceProviderType,
  HotelServicesFilters as ServiceFilters,
} from "@/types/hotelService";

export const ALL_STATUES = "all statuses";
export const ALL_CATEGORIES = "all categories";
export const ALL_TYPES = "all types";
export const ANY_PRICE = "price";
export const ALL_PROVIDER_TYPES = "all provider types";
const DEFAULT_PARTNERS_SORT = "name:asc";

// Sorting
type SortField = "number" | "type" | "status" | "floor";
type SortDirection = "asc" | "desc" | null;

interface ServiceContextType {
  // Query Hooks
  useServices: (
    hotelId: string,
    page?: number,
    limit?: number,
    filters?: ServiceFilters,
  ) => ReturnType<typeof useServices>;
  useService: (id: string, hotelId: string) => ReturnType<typeof useService>;

  // Mutation Hooks
  useCreateService: () => ReturnType<typeof useCreateService>;
  useUpdateService: (
    id: string,
    hotelId: string,
  ) => ReturnType<typeof useUpdateService>;
  useDeleteService: (
    id: string,
    hotelId: string,
  ) => ReturnType<typeof useDeleteService>;
  useActivateService: (
    id: string,
    hotelId: string,
  ) => ReturnType<typeof useActivateService>;
  useDeactivateService: (
    id: string,
    hotelId: string,
  ) => ReturnType<typeof useDeactivateService>;
  useCheckinServices: (
    hotelId: string,
  ) => ReturnType<typeof useCheckinServices>;

  // Filters
  filters: {
    providerType: string;
    price: string;
    category: string;
    partnerTagIds: number[];
    status: string;
    responsibleIds: string[];
    currentPage: number;
    sortField: SortField | null;
    sortDirection: SortDirection;
  };

  // Actions
  actions: {
    setProviderType: (type: string) => void;
    setPrice: (price: string) => void;
    setCategory: (category: string) => void;
    setPartnerTagIds: (tagIds: number[]) => void;
    setStatus: (status: string) => void;
    setResponsibleIds: (ids: string[]) => void;
    setCurrentPage: (page: number) => void;
    setSortField: (field: SortField | null) => void;
    setSortDirection: (dir: SortDirection) => void;
    handleSort: (field: string) => void;
    handleFilterChange: (
      setter: (value: string) => void,
      value: string,
    ) => void;
    resetFilters: (providerType?: string) => void;
  };

  // Computed
  computed: {
    apiFilters: ServiceFilters;
    limit: number;
  };
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

interface ServiceProviderProps {
  children: ReactNode;
}

function applyCategoryFilters(
  filters: ServiceFilters,
  providerType: string,
  category: string,
  partnerTagIds: number[],
) {
  if (providerType === HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE) {
    if (partnerTagIds.length) filters.tagIds = partnerTagIds;
    return;
  }

  if (category !== ALL_CATEGORIES) {
    filters.categoryId = category;
  }
}

function applyResponsibleFilters(
  filters: ServiceFilters,
  providerType: string,
  responsibleIds: string[],
) {
  if (
    providerType !== HotelServiceProviderType.GUEST_SERVICE ||
    !responsibleIds.length
  ) {
    return;
  }

  const staffIds = responsibleIds
    .filter((item) => item.startsWith("staff:"))
    .map((item) => item.replace("staff:", ""));
  const teamIds = responsibleIds
    .filter((item) => item.startsWith("team:"))
    .map((item) => item.replace("team:", ""));

  if (staffIds.length) filters.staffIds = staffIds;
  if (teamIds.length) filters.teamIds = teamIds;
}

function applySortFilters(
  filters: ServiceFilters,
  providerType: string,
  sortField: SortField | null,
  sortDirection: SortDirection,
) {
  if (sortField && sortDirection) {
    filters.sort = `${sortField}:${sortDirection}`;
    return;
  }

  if (providerType === HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE) {
    filters.sort = DEFAULT_PARTNERS_SORT;
  }
}

export const ServiceProvider: React.FC<ServiceProviderProps> = ({
  children,
}) => {
  const [providerType, setProviderType] = useState<string>(
    HotelServiceProviderType.GUEST_SERVICE,
  );
  const [price, setPrice] = useState<string>(ANY_PRICE);
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [partnerTagIds, setPartnerTagIds] = useState<number[]>([]);
  const [status, setStatus] = useState<string>(ALL_STATUES);
  const [responsibleIds, setResponsibleIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const limit = 10;

  const handleSort = (field: string) => {
    const newSortField = field as SortField;

    if (sortField === newSortField) {
      let nextDirection: SortDirection;

      if (sortDirection === "asc") {
        nextDirection = "desc";
      } else if (sortDirection === "desc") {
        nextDirection = null;
      } else {
        nextDirection = "asc";
      }

      setSortDirection(nextDirection);

      if (nextDirection === null) {
        setSortField(null);
      }
    } else {
      setSortField(newSortField);
      setSortDirection("asc");
    }

    setCurrentPage(1);
  };

  const handleFilterChange = (
    setter: (value: string) => void,
    value: string,
  ) => {
    setter(value);
    setCurrentPage(1);
  };

  const resetFilters = (
    nextProviderType: string = HotelServiceProviderType.GUEST_SERVICE,
  ) => {
    setProviderType(nextProviderType);
    setStatus(ALL_STATUES);
    setCategory(ALL_CATEGORIES);
    setPartnerTagIds([]);
    setPrice(ANY_PRICE);
    setResponsibleIds([]);
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const apiFilters: ServiceFilters = useMemo(() => {
    const filters: ServiceFilters = {};
    if (price !== ANY_PRICE) filters.price = price;
    applyCategoryFilters(filters, providerType, category, partnerTagIds);
    if (status !== ALL_STATUES) filters.status = status;
    if (providerType) filters.providerType = providerType;
    applyResponsibleFilters(filters, providerType, responsibleIds);
    applySortFilters(filters, providerType, sortField, sortDirection);
    filters.page = currentPage;
    filters.limit = limit;
    return filters;
  }, [
    providerType,
    price,
    category,
    partnerTagIds,
    status,
    responsibleIds,
    sortField,
    sortDirection,
    currentPage,
  ]);

  const contextValue: ServiceContextType = {
    useServices,
    useService,
    useCreateService,
    useUpdateService,
    useDeleteService,
    useActivateService,
    useDeactivateService,
    useCheckinServices,
    filters: {
      providerType,
      price,
      category,
      partnerTagIds,
      status,
      responsibleIds,
      currentPage,
      sortField,
      sortDirection,
    },
    actions: {
      setProviderType,
      setPrice,
      setCategory,
      setPartnerTagIds,
      setStatus,
      setResponsibleIds,
      setCurrentPage,
      setSortField,
      setSortDirection,
      handleSort,
      handleFilterChange,
      resetFilters,
    },
    computed: {
      apiFilters,
      limit,
    },
  };

  return (
    <ServiceContext.Provider value={contextValue}>
      {children}
    </ServiceContext.Provider>
  );
};

export const useServiceContext = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error("useServiceContext must be used within a ServiceProvider");
  }
  return context;
};
